import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { concat, of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';

import { DashboardServiceService } from '@containers/reports/dashboard/services/dashboard-service.service';
import { DateFormatPipe } from '@pipes/date-format.pipe';
import { set_date_type_to_custom } from '@rpt-ngrx/actions/filters.action';
import { selectFilterState } from '@rpt-ngrx/selectors/filters.selector';
import { selectUser } from '@rpt-ngrx/selectors/session.selector';
import { CustomReportsService, NotificationService, ReportsService } from '@shared/services';

import {
    add_report,
    add_report_failure,
    add_report_success,
    add_report_to_history,
    clear_selected_row_essential_data,
    close_active_report,
    get_reports,
    get_reports_failure,
    get_reports_success,
    get_stats_failure,
    get_stats_success,
    make_default,
    make_default_failure,
    make_default_success,
    remove_report,
    remove_reports_from_history,
    reset_default,
    reset_default_failure,
    reset_default_success,
    select_report,
    select_report_failure,
    select_report_success,
    set_report_etl_statistics,
    set_report_etl_statistics_failure,
    set_report_etl_statistics_success,
    set_report_filters,
    set_report_filters_failure,
    set_report_filters_success,
    set_report_url,
    set_report_url_failure,
    set_report_url_success,
} from '../actions/reports.action';
import { select_sidebar_item } from '../actions/ui.action';

@Injectable()
export class ReportsEffects {
    getReports$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_reports),
            mergeMap(() =>
                this.reportsService.fetchReports().pipe(
                    map(({ reports }) => {
                        return get_reports_success({ reports });
                    }),
                    catchError(async () => get_reports_failure),
                ),
            ),
        );
    });

    getDashboardStats$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(get_reports),
            mergeMap(() =>
                this.dashboardService.getStats(this.customReportsService.getCompanyId()).pipe(
                    map(({ data }) => {
                        return get_stats_success({
                            title: data.title,
                            stats: data.named_calculations,
                            execute_automatically: data.execute_automatically,
                            show_history_data: data.show_history_data,
                            hasActiveLaborMatrix: data.activeLaborOption,
                        });
                    }),
                    catchError(async () => get_stats_failure),
                ),
            ),
        );
    });

    selectReport$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(select_report),
            map(({ report, filters }) => select_report_success({ report, filters })),
            catchError(async () => select_report_failure),
        );
    });

    setDateTypeToCustom$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(select_report),
            concatLatestFrom(() => this.store.select(selectFilterState)),
            filter(([_action, stateData]) => !!stateData?.selectedDateForMultiDate),
            map(([action, stateData]) =>
                set_date_type_to_custom({
                    selectedDate: stateData?.selectedDateForMultiDate,
                    isDrillDown: action.isDrillDown,
                }),
            ),
        );
    });

    setReportEtlStatistics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(set_report_etl_statistics),
            mergeMap(({ reportName }) =>
                this.reportsService.fetchEtlStatisitcs(reportName).pipe(
                    map(({ jobStatus, jobStarted, jobFinished, jobDuration }) => {
                        if (jobStarted) {
                            jobStarted = this.dateFormatPipe.transform(jobStarted, 'L LT');
                        }
                        if (jobFinished) {
                            jobFinished = this.dateFormatPipe.transform(jobFinished, 'L LT');
                        }
                        return set_report_etl_statistics_success({ jobStatus, jobStarted, jobFinished, jobDuration });
                    }),
                ),
            ),
            catchError(async () => set_report_etl_statistics_failure),
        );
    });

    setReportFilters$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(set_report_filters),
            map(({ reportFilters }) => set_report_filters_success({ reportFilters })),
            catchError(async () => set_report_filters_failure),
        );
    });

    setReportUrl$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(set_report_url),
            map(({ reportUrl }) => set_report_url_success({ reportUrl })),
            catchError(async () => set_report_url_failure),
        );
    });

    addReport$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(add_report),
            mergeMap(({ companyId, gridOptions, parentReport, permissions, detailPageOptions }) => {
                return this.customReportsService
                    .publishReport(companyId, gridOptions, parentReport, permissions, detailPageOptions)
                    .pipe(
                        map(() => {
                            this.notificationService.success(this.translations.instant('SUCCESS.PUBLISHED_SUCCESSFUL'));
                            return add_report_success();
                        }),
                        catchError(async () => add_report_failure),
                    );
            }),
        );
    });

    makeDefault$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(make_default),
            mergeMap(({ companyId, gridOptions, report }) => {
                return this.store.select(selectUser).pipe(
                    mergeMap(({ id }) => {
                        return this.customReportsService.makeDefaultSchema(companyId, id, gridOptions, report).pipe(
                            map(() => {
                                this.notificationService.success(
                                    this.translations.instant('SUCCESS.MAKE_DEFAULT_SUCCESSFUL'),
                                );
                                return make_default_success({ report, gridOptions });
                            }),
                            catchError(async () => make_default_failure),
                        );
                    }),
                );
            }),
        );
    });

    makeDefaultError$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(make_default_failure),
                tap(() => {
                    this.notificationService.error(this.translations.instant('ERROR.MAKE_DEFAULT_UNSUCCESSFUL'));
                }),
            );
        },
        { dispatch: false },
    );

    resetDefault$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(reset_default),
            mergeMap(({ companyId, report }) => {
                return this.store.select(selectUser).pipe(
                    mergeMap(({ id }) => {
                        return this.customReportsService.resetDefaultSchema(companyId, id, report).pipe(
                            map(() => {
                                this.notificationService.success(
                                    this.translations.instant('SUCCESS.RESET_DEFAULT_SUCCESSFUL'),
                                );
                                return reset_default_success({ report });
                            }),
                            catchError(async () => reset_default_failure),
                        );
                    }),
                );
            }),
        );
    });

    resetDefaultError$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(make_default_failure),
                tap(() => {
                    this.notificationService.error(this.translations.instant('ERROR.RESET_DEFAULT_UNSUCCESSFUL'));
                }),
            );
        },
        { dispatch: false },
    );

    clearSelectedRowData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(add_report_to_history),
            map(() => clear_selected_row_essential_data()),
        );
    });

    closeActiveReport$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(close_active_report),
            mergeMap(() =>
                concat(
                    of(remove_reports_from_history({ index: 0 })),
                    of(remove_report()),
                    of(select_sidebar_item({ selectedMenuItem: null })),
                ),
            ),
        );
    });

    constructor(
        private actions$: Actions,
        private store: Store,
        private reportsService: ReportsService,
        private notificationService: NotificationService,
        private customReportsService: CustomReportsService,
        private translations: TranslateService,
        private dashboardService: DashboardServiceService,
        private dateFormatPipe: DateFormatPipe,
    ) {}
}
